import type {
  GameState,
  AIDifficulty,
  WarriorInstance,
  BoardPosition,
  PlayerId,
} from '@/types';
import { BoardManager } from '@core/BoardManager';

/**
 * AI action types
 */
export type AIActionType = 'move' | 'dash' | 'attack' | 'heal';

export interface AITurn {
  readonly warriorId: string;
  readonly action: AIActionType;
  readonly position?: BoardPosition; // For move/dash actions
}

/**
 * Progress callback for AI thinking visualization
 */
export interface AIThinkingProgress {
  currentMove: number;
  totalMoves: number;
  currentWarrior: string;
  currentAction: string;
}

export type AIProgressCallback = (progress: AIThinkingProgress) => void;

/**
 * Helper to yield to the event loop for UI updates
 */
const yieldToUI = (): Promise<void> => new Promise(resolve => setTimeout(resolve, 0));

/**
 * Simple strategy configuration
 */
interface AIStrategy {
  readonly lookAheadDepth: number; // Ply depth (2 = 1 turn pair)
}

const STRATEGIES: Record<AIDifficulty, AIStrategy> = {
  easy: { lookAheadDepth: 2 },   // 1 turn pair (my move + their response)
  medium: { lookAheadDepth: 4 }, // 2 turn pairs
  hard: { lookAheadDepth: 6 },   // 3 turn pairs
};

/**
 * Simplified AI opponent for 1-player mode
 *
 * Uses pure minimax with alpha-beta pruning and a simple scoring function
 * based on material (health) and power potential (damage/healing weighted by cooldown).
 */
export class AIPlayer {
  private strategy: AIStrategy;

  constructor(difficulty: AIDifficulty) {
    this.strategy = STRATEGIES[difficulty];
  }

  /**
   * Reset tracking state (call when starting a new game)
   */
  resetState(): void {
    // No state to reset in simplified AI
  }

  /**
   * Compute the AI's turn (select warrior + action)
   */
  async computeTurn(state: GameState, progressCallback?: AIProgressCallback): Promise<AITurn> {
    const aiWarriors = this.getAliveWarriors(state, 'player2');

    if (aiWarriors.length === 0) {
      throw new Error('No AI warriors available');
    }

    // Generate all possible moves
    const allMoves = this.generateAllMoves(state, aiWarriors);

    if (allMoves.length === 0) {
      return this.getFallbackMove(aiWarriors, state);
    }

    // Score each move with minimax lookahead
    let bestTurn: AITurn | null = null;
    let bestScore = -Infinity;
    let lastYieldTime = Date.now();

    for (let i = 0; i < allMoves.length; i++) {
      const move = allMoves[i];

      // Progress callback and yield to UI periodically
      const now = Date.now();
      if (progressCallback && now - lastYieldTime >= 50) {
        const warrior = aiWarriors.find(w => w.instanceId === move.warriorId);
        progressCallback({
          currentMove: i,
          totalMoves: allMoves.length,
          currentWarrior: warrior?.definition.name || move.warriorId,
          currentAction: move.action,
        });
        await yieldToUI();
        lastYieldTime = now;
      }

      // Simulate this move
      const newState = this.simulateTurn(state, move);

      // Evaluate with minimax (opponent's turn next, so minimizing)
      const score = this.minimax(
        newState,
        this.strategy.lookAheadDepth - 1,
        -Infinity,
        Infinity,
        false // Enemy moves next
      );

      if (score > bestScore) {
        bestScore = score;
        bestTurn = move;
      }
    }

    return bestTurn || this.getFallbackMove(aiWarriors, state);
  }

  /**
   * Generate all possible moves for a set of warriors
   */
  private generateAllMoves(state: GameState, warriors: WarriorInstance[]): AITurn[] {
    const moves: AITurn[] = [];

    for (const warrior of warriors) {
      const actions = BoardManager.getAvailableActions(
        warrior,
        state.board,
        this.getAllWarriors(state)
      );

      // Generate attack (if available and has targets)
      if (actions.canAttack && actions.attackTargetPositions.length > 0) {
        const enemyPlayer = warrior.ownerId === 'player2' ? 'player1' : 'player2';
        const enemies = this.getAliveWarriors(state, enemyPlayer);
        const hasTargets = enemies.some(e =>
          e.position && actions.attackTargetPositions.some(
            p => p.row === e.position!.row && p.col === e.position!.col
          )
        );

        if (hasTargets) {
          moves.push({
            warriorId: warrior.instanceId,
            action: 'attack',
          });
        }
      }

      // Generate heal
      if (actions.canHeal) {
        moves.push({
          warriorId: warrior.instanceId,
          action: 'heal',
        });
      }

      // Generate moves
      for (const pos of actions.validMovePositions) {
        moves.push({
          warriorId: warrior.instanceId,
          action: 'move',
          position: pos,
        });
      }

      // Generate dashes
      for (const pos of actions.validDashPositions) {
        moves.push({
          warriorId: warrior.instanceId,
          action: 'dash',
          position: pos,
        });
      }
    }

    return moves;
  }

  /**
   * Minimax with alpha-beta pruning
   */
  private minimax(
    state: GameState,
    depth: number,
    alpha: number,
    beta: number,
    isMaximizing: boolean
  ): number {
    // Base case: reached leaf node
    if (depth === 0) {
      return this.evaluateState(state);
    }

    const aiWarriors = this.getAliveWarriors(state, 'player2');
    const enemyWarriors = this.getAliveWarriors(state, 'player1');

    // Terminal states
    if (aiWarriors.length === 0) return -10000;
    if (enemyWarriors.length === 0) return 10000;

    const currentPlayer = isMaximizing ? 'player2' : 'player1';
    const warriors = this.getAliveWarriors(state, currentPlayer);
    const moves = this.generateAllMoves(state, warriors);

    if (moves.length === 0) {
      return this.evaluateState(state);
    }

    if (isMaximizing) {
      let maxEval = -Infinity;

      for (const move of moves) {
        const newState = this.simulateTurn(state, move);
        const evalScore = this.minimax(newState, depth - 1, alpha, beta, false);

        maxEval = Math.max(maxEval, evalScore);
        alpha = Math.max(alpha, evalScore);
        if (beta <= alpha) break; // Beta cutoff
      }

      return maxEval;
    } else {
      let minEval = Infinity;

      for (const move of moves) {
        const newState = this.simulateTurn(state, move);
        const evalScore = this.minimax(newState, depth - 1, alpha, beta, true);

        minEval = Math.min(minEval, evalScore);
        beta = Math.min(beta, evalScore);
        if (beta <= alpha) break; // Alpha cutoff
      }

      return minEval;
    }
  }

  /**
   * Pure state evaluation based on material and power potential
   * Returns score from AI (player2) perspective (positive = good for AI)
   */
  private evaluateState(state: GameState): number {
    const aiWarriors = this.getAliveWarriors(state, 'player2');
    const enemyWarriors = this.getAliveWarriors(state, 'player1');

    // Terminal states
    if (aiWarriors.length === 0) return -10000;
    if (enemyWarriors.length === 0) return 10000;

    let score = 0;

    // === MATERIAL (Health) ===
    const aiHealth = aiWarriors.reduce((sum, w) => sum + w.currentHealth, 0);
    const enemyHealth = enemyWarriors.reduce((sum, w) => sum + w.currentHealth, 0);
    score += (aiHealth - enemyHealth) * 10;

    // === POWER POTENTIAL (Damage weighted by cooldown) ===
    const aiDamagePotential = this.calculateDamagePotential(aiWarriors);
    const enemyDamagePotential = this.calculateDamagePotential(enemyWarriors);
    score += (aiDamagePotential - enemyDamagePotential) * 1;

    // === HEALING POTENTIAL (Healing weighted by cooldown) ===
    const aiHealPotential = this.calculateHealPotential(aiWarriors);
    const enemyHealPotential = this.calculateHealPotential(enemyWarriors);
    score += (aiHealPotential - enemyHealPotential) * 1;

    return score;
  }

  /**
   * Calculate total damage potential weighted by cooldown readiness
   * Ready = 100%, 1 turn away = 50%, 2+ turns = 25%
   */
  private calculateDamagePotential(warriors: WarriorInstance[]): number {
    return warriors.reduce((total, warrior) => {
      const baseDamage = warrior.definition.stats.damage;
      const attackFreq = warrior.definition.stats.attackFrequency;

      // Calculate readiness weight
      let weight: number;
      if (attackFreq === 0) {
        weight = 1.0; // Always ready
      } else {
        const turnsUntilReady = Math.max(0, attackFreq - warrior.attackCooldown);
        if (turnsUntilReady === 0) weight = 1.0; // Ready now
        else if (turnsUntilReady === 1) weight = 0.5; // 1 turn away
        else weight = 0.25; // 2+ turns away
      }

      return total + baseDamage * weight;
    }, 0);
  }

  /**
   * Calculate total healing potential weighted by cooldown readiness
   */
  private calculateHealPotential(warriors: WarriorInstance[]): number {
    return warriors.reduce((total, warrior) => {
      const healPower = warrior.definition.stats.healingPower;
      if (healPower === 0) return total;

      const healFreq = warrior.definition.stats.healingFrequency;

      // Calculate readiness weight
      let weight: number;
      if (healFreq === 0) {
        weight = 1.0; // Always ready
      } else {
        const turnsUntilReady = Math.max(0, healFreq - warrior.healCooldown);
        if (turnsUntilReady === 0) weight = 1.0;
        else if (turnsUntilReady === 1) weight = 0.5;
        else weight = 0.25;
      }

      return total + healPower * weight;
    }, 0);
  }

  /**
   * Simulate executing a turn and return the resulting game state
   */
  private simulateTurn(state: GameState, turn: AITurn): GameState {
    // Deep clone the state
    const newState: GameState = JSON.parse(JSON.stringify(state));

    // Find the warrior - search both player lists to be robust
    let warrior = newState.players.player1.warriors.find(w => w.instanceId === turn.warriorId);
    if (!warrior) {
      warrior = newState.players.player2.warriors.find(w => w.instanceId === turn.warriorId);
    }

    if (!warrior || !warrior.position) return newState;

    const isPlayer2 = warrior.ownerId === 'player2';
    const enemies = isPlayer2 ? newState.players.player1.warriors : newState.players.player2.warriors;

    switch (turn.action) {
      case 'move':
      case 'dash':
        if (turn.position) {
          // Update board
          newState.board[warrior.position.row][warrior.position.col] = null;
          newState.board[turn.position.row][turn.position.col] = warrior.instanceId;
          warrior.position = turn.position;
        }
        if (turn.action === 'dash') {
          warrior.dashCooldown = 0;
        }
        break;

      case 'attack': {
        const attackPositions = BoardManager.getAttackTargetPositions(
          warrior.position,
          warrior.definition.attackPattern,
          isPlayer2
        );

        for (const enemy of enemies) {
          if (!enemy.position) continue;
          const isHit = attackPositions.some(
            pos => pos.row === enemy.position!.row && pos.col === enemy.position!.col
          );
          if (isHit) {
            enemy.currentHealth -= warrior.definition.stats.damage;
            if (enemy.currentHealth <= 0) {
              newState.board[enemy.position.row][enemy.position.col] = null;
              enemy.position = null;
            }
          }
        }
        warrior.attackCooldown = 0;
        break;
      }

      case 'heal': {
        const allies = isPlayer2 ? newState.players.player2.warriors : newState.players.player1.warriors;
        for (const ally of allies) {
          if (ally.position) {
            ally.currentHealth = Math.min(
              ally.currentHealth + warrior.definition.stats.healingPower,
              ally.definition.stats.health
            );
          }
        }
        warrior.healCooldown = 0;
        break;
      }
    }

    // Increment cooldowns for the acting warrior
    warrior.attackCooldown++;
    warrior.healCooldown++;
    warrior.dashCooldown++;

    return newState;
  }

  /**
   * Get a fallback move when no valid moves found
   */
  private getFallbackMove(warriors: WarriorInstance[], state: GameState): AITurn {
    const randomWarrior = warriors[Math.floor(Math.random() * warriors.length)];
    const actions = BoardManager.getAvailableActions(
      randomWarrior,
      state.board,
      this.getAllWarriors(state)
    );

    if (actions.validMovePositions.length > 0) {
      return {
        warriorId: randomWarrior.instanceId,
        action: 'move',
        position: actions.validMovePositions[0],
      };
    } else if (actions.canHeal) {
      return {
        warriorId: randomWarrior.instanceId,
        action: 'heal',
      };
    } else {
      // Last resort
      return {
        warriorId: randomWarrior.instanceId,
        action: 'move',
        position: { row: 2, col: 2 },
      };
    }
  }

  /**
   * Get alive warriors for a player
   */
  private getAliveWarriors(state: GameState, playerId: PlayerId): WarriorInstance[] {
    const player = playerId === 'player1' ? state.players.player1 : state.players.player2;
    return player.warriors.filter(w => w.position !== null);
  }

  /**
   * Get all warriors in the game
   */
  private getAllWarriors(state: GameState): WarriorInstance[] {
    return [...state.players.player1.warriors, ...state.players.player2.warriors];
  }
}
